/**
 * Frontend-Fehler an das Backend melden.
 *
 * Bis heute gab es diesen Weg vollständig — Client-Funktion, Endpunkt,
 * Protokollkategorie `frontend` — und **null Aufrufer**. Ein Renderfehler landete
 * ausschließlich in der Browserkonsole des Nutzers, also genau dort, wo ihn niemand
 * sieht, der ihn beheben könnte.
 *
 * Diese Datei ist die eine Stelle, über die gemeldet wird. Sie hält drei Dinge
 * zusammen, die sonst an jedem Aufrufer einzeln stehen müssten:
 *
 *  - **Eigenschutz:** Das Melden darf nie selbst einen Fehler auslösen, der wieder
 *    hier landet. Alles ist in try/catch, und es wird nichts geworfen.
 *  - **Drosselung:** Ein Fehler in einer Renderschleife erzeugt sonst hunderte
 *    Meldungen je Sekunde. Gleiche Meldungen werden zusammengefasst, insgesamt gilt
 *    eine Obergrenze je Sitzung.
 *  - **Kein Melden ohne Anmeldung:** Der Endpunkt verlangt eine Sitzung. Ohne sie
 *    entstünde nur ein 401, der wiederum eine Meldung auslösen könnte.
 */
import { systemLogsApi } from './api';
import { useAuthStore } from '../stores/authStore';

/** Höchstens so viele Meldungen je geladener Seite — Schutz gegen Endlosschleifen. */
const MAX_JE_SITZUNG = 20;
/** Dieselbe Meldung höchstens einmal je Minute. */
const WIEDERHOLUNGSSPERRE_MS = 60_000;

let gesendet = 0;
const zuletzt = new Map<string, number>();
let imMeldepfad = false;

/** Grober Schlüssel zum Zusammenfassen gleicher Meldungen. */
function schluessel(meldung: string): string {
  return meldung.replace(/\d+/g, '<n>').slice(0, 200);
}

/**
 * Gleiche Quellen wie der Axios-Interceptor in `api.ts`: erst der Agenten-Speicher,
 * sonst die Ablage des Kundenportals. Ein eigener Weg würde hier irgendwann
 * auseinanderlaufen.
 */
function angemeldet(): boolean {
  try {
    if (useAuthStore.getState().accessToken) return true;
    const gespeichert = localStorage.getItem('customer-auth-storage');
    if (!gespeichert) return false;
    return !!JSON.parse(gespeichert)?.state?.token;
  } catch {
    return false;
  }
}

export function fehlerMelden(
  meldung: string,
  details?: { stack?: string; komponente?: string; url?: string }
): void {
  if (imMeldepfad) return;
  if (!meldung) return;
  if (gesendet >= MAX_JE_SITZUNG) return;
  if (!angemeldet()) return;

  const k = schluessel(meldung);
  const jetzt = Date.now();
  const vorher = zuletzt.get(k) ?? 0;
  if (jetzt - vorher < WIEDERHOLUNGSSPERRE_MS) return;
  zuletzt.set(k, jetzt);

  imMeldepfad = true;
  gesendet++;
  try {
    const beschreibung = [
      details?.komponente ? `Komponente: ${details.komponente}` : '',
      `Adresse: ${details?.url ?? window.location.pathname}`,
      details?.stack ? `\n${details.stack.slice(0, 2000)}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    systemLogsApi
      .logFrontendError({ level: 'error', message: meldung.slice(0, 500), details: beschreibung })
      .catch(() => {
        // Bewusst leer: Schlägt das Melden fehl, ist das kein Grund, dem Nutzer etwas
        // anzuzeigen oder es erneut zu versuchen. Ein Meldeweg, der bei Störungen
        // laut wird, verstärkt genau die Störung.
      })
      .finally(() => {
        imMeldepfad = false;
      });
  } catch {
    imMeldepfad = false;
  }
}
